/**
 * MapPage - The main mind map editing page
 */

import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Settings,
  Sparkles,
  Download,
  Loader2,
} from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { useMapStore } from '../stores/mapStore';
import MapCanvas from '../components/map/MapCanvas';
import ChatSidebar from '../components/chat/ChatSidebar';
import TimeSlider from '../components/timeline/TimeSlider';
import GhostNodesPanel from '../components/ui/GhostNodesPanel';
import MapToolbar from '../components/ui/MapToolbar';
import EditNodeModal from '../components/ui/EditNodeModal';
import AttachmentModal from '../components/ui/AttachmentModal';
import SettingsModal from '../components/ui/SettingsModal';

const MapPage = () => {
  const { mapId } = useParams();
  const navigate = useNavigate();
  const {
    currentMap,
    isLoadingNodes,
    loadMap,
    ghostNodes,
    isAnalyzingGaps,
    analyzeGaps,
    editModalNodeId,
    attachmentModalNodeId,
    isSettingsOpen,
    openSettings,
    exportMap,
  } = useMapStore();

  // Load map on mount
  useEffect(() => {
    if (mapId) {
      loadMap(mapId);
    }
  }, [mapId, loadMap]);

  if (isLoadingNodes && !currentMap) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="text-indigo-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading your mind map...</p>
        </div>
      </div>
    );
  }

  if (!currentMap) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Map not found</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-750 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-950">
      {/* Header */}
      <header className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3 bg-gray-900/80 backdrop-blur border-b border-gray-800 z-10">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft size={20} className="text-gray-400" />
          </button>

          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-semibold text-white truncate">
              {currentMap.title}
            </h1>
            {currentMap.is_main && (
              <span className="text-xs text-indigo-400">Main Brain</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Gap Analysis button */}
          <button
            onClick={analyzeGaps}
            disabled={isAnalyzingGaps}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-purple-600/20 text-purple-300 rounded-lg hover:bg-purple-600/30 disabled:opacity-50 transition-colors"
          >
            {isAnalyzingGaps ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Sparkles size={16} />
            )}
            <span className="text-sm hidden sm:inline">Analyze Gaps</span>
          </button>

          <button
            onClick={exportMap}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors hidden sm:block"
            title="Export Map"
          >
            <Download size={18} className="text-gray-400" />
          </button>

          <button
            onClick={openSettings}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings size={18} className="text-gray-400" />
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 relative overflow-hidden">
        <MapCanvas />
        <MapToolbar />
        <ChatSidebar />
        <GhostNodesPanel />
      </div>

      {/* Time travel slider */}
      <TimeSlider />

      {/* Modals */}
      <AnimatePresence>
        {editModalNodeId && <EditNodeModal />}
        {attachmentModalNodeId && <AttachmentModal />}
        {isSettingsOpen && <SettingsModal />}
      </AnimatePresence>
    </div>
  );
};

export default MapPage;
